// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ButtonPresentationPropsDefault from "@insite/mobius/Button/presentationProps";
import { IconWrapper } from "@insite/mobius/Icon";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { xBaseTheme } from "@sc/XDL/types";
import { color } from "@sc/XDL/Utilities";
import { css } from "styled-components";

export const buttonTransparent = css`
    &,
    &:focus:not([disabled]),
    &:active:not([disabled]),
    &:disabled,
    &:focus:hover,
    &:hover:not(:disabled) {
        border: none;
        background-color: transparent;
        box-shadow: none;
        outline: 0;
        transform: translate(0, 0);
        min-height: initial;
    }
`;

const buttonColors: { [key: string]: { main: string; hover: string; text: string; focus: string } } = {
    primary: {
        main: "primary.main",
        hover: "common.ui8",
        text: "common.ui1",
        focus: "common.ui9",
    },
    secondary: {
        main: "primary.main",
        hover: "common.ui12",
        text: "common.ui8",
        focus: "common.ui9",
    },
    tertiary: {
        main: "common.ui1",
        hover: "common.ui12",
        text: "common.ui8",
        focus: "common.ui9",
    },
};

const IconStyle = css`
    ${IconWrapper} {
        color: ${color("common.ui4")};
        opacity: 0.66;
    }
`;
const sharedButtonStyle = {
    ...ButtonPresentationPropsDefault,
    color: buttonColors.secondary.main,
    buttonType: "outline",
    mergeCss: true,
    typographyProps: {
        weight: "bold",
        css: css`
            letter-spacing: 0;
            text-align: center;
        `,
    },
    css: css`
        border-width: 1px;
        border-style: solid;
        border-radius: 4px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-color: ${color(buttonColors.secondary.main)};
        color: ${color(buttonColors.secondary.text)};

        &:not(:disabled):hover {
            color: ${color(buttonColors.secondary.hover)};
            background-color: ${color(buttonColors.secondary.hover)};
            border-color: ${color("common.ui8")};
            ${TypographyStyle} {
                color: ${color(buttonColors.secondary.text)};
            }
        }
        &:not(:disabled):focus {
            outline: none;
            background-color: ${color(buttonColors.secondary.focus)};
            border-color: ${color(buttonColors.secondary.focus)};
            ${TypographyStyle} {
                color: ${color(buttonColors.primary.text)};
            }
        }
        &:disabled {
            color: ${color(buttonColors.secondary.main)};
            border-color: ${color(buttonColors.secondary.main)};
            opacity: 0.33;
        }
    `,
};

export default {
    primary: {
        ...ButtonPresentationPropsDefault,
        mergeCss: true,
        color: buttonColors.primary.main,
        buttonType: "solid",
        typographyProps: {
            weight: "bold",
            css: css`
                letter-spacing: 0;
                text-align: center;
            `,
        },
        css: css`
            border-width: 1px;
            border-style: solid;
            border-radius: 4px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            &:not(:disabled):hover {
                background: ${color(buttonColors.primary.hover)};
                border-color: ${color(buttonColors.primary.hover)};
                ${TypographyStyle} {
                    color: ${color("common.ui1")};
                }
            }
            &:not(:disabled):focus {
                outline: none;
                background-color: ${color(buttonColors.primary.focus)};
                border-color: ${color(buttonColors.primary.focus)};
                ${TypographyStyle} {
                    color: ${color(buttonColors.primary.text)};
                }
            }
            &:disabled {
                background-color: ${color(buttonColors.primary.main)};
                border-color: ${color(buttonColors.primary.main)};
                opacity: 0.33;
            }
        `,
    },
    secondary: {
        ...sharedButtonStyle,
    },
    tertiary: {
        ...sharedButtonStyle,
    },
} as xBaseTheme["button"];
